import { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { cn } from '../../lib/utils';
import { Spinner } from '../ui';

/**
 * PhotoUploader Component
 *
 * Drag & drop photo upload with preview, reorder, and delete.
 * Supports 1-8 images per raffle.
 *
 * @param {string[]} photos - Array of photo URLs
 * @param {function} onChange - Callback when photos change
 * @param {function} onUpload - Async function to upload a file, returns URL
 * @param {number} maxPhotos - Maximum allowed photos (default: 8)
 */
export default function PhotoUploader({
  photos = [],
  onChange,
  onUpload,
  maxPhotos = 8,
  error,
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [draggedIndex, setDraggedIndex] = useState(null);
  const dragOverIndex = useRef(null);

  const canUploadMore = photos.length < maxPhotos;

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!onUpload) return;

      const filesToUpload = acceptedFiles.slice(0, maxPhotos - photos.length);
      if (filesToUpload.length === 0) return;

      setUploading(true);

      try {
        const uploadPromises = filesToUpload.map(async (file, index) => {
          const tempId = `uploading-${Date.now()}-${index}`;
          setUploadProgress((prev) => ({ ...prev, [tempId]: 0 }));

          try {
            const url = await onUpload(file, (progress) => {
              setUploadProgress((prev) => ({ ...prev, [tempId]: progress }));
            });
            return url;
          } finally {
            setUploadProgress((prev) => {
              const { [tempId]: _, ...rest } = prev;
              return rest;
            });
          }
        });

        const newUrls = await Promise.all(uploadPromises);
        onChange([...photos, ...newUrls.filter(Boolean)]);
      } catch (error) {
        console.error('Upload error:', error);
      } finally {
        setUploading(false);
      }
    },
    [photos, onChange, onUpload, maxPhotos]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: !canUploadMore || uploading,
  });

  function handleRemove(index) {
    const newPhotos = photos.filter((_, i) => i !== index);
    onChange(newPhotos);
  }

  function handleDragStart(index) {
    setDraggedIndex(index);
  }

  function handleDragOver(e, index) {
    e.preventDefault();
    dragOverIndex.current = index;
  }

  function handleDragEnd() {
    if (draggedIndex !== null && dragOverIndex.current !== null && draggedIndex !== dragOverIndex.current) {
      const newPhotos = [...photos];
      const [removed] = newPhotos.splice(draggedIndex, 1);
      newPhotos.splice(dragOverIndex.current, 0, removed);
      onChange(newPhotos);
    }
    setDraggedIndex(null);
    dragOverIndex.current = null;
  }

  return (
    <div className="space-y-4">
      {/* Photo Grid */}
      {photos.length > 0 && console.log('[PhotoUploader] photos:', photos)}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {photos.map((photo, index) => (
            <div
              key={photo}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={cn(
                'relative aspect-square rounded-lg overflow-hidden border-2 cursor-move group',
                index === 0 ? 'col-span-2 row-span-2 border-primary' : 'border-midnight-lighter',
                draggedIndex === index && 'opacity-50'
              )}
            >
              <img
                src={photo}
                alt={`Photo ${index + 1}`}
                className="w-full h-full object-cover"
              />

              {/* Cover badge */}
              {index === 0 && (
                <div className="absolute top-2 left-2 bg-primary text-white text-xs font-semibold px-2 py-0.5 rounded">
                  Cover
                </div>
              )}

              {/* Delete button */}
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="absolute top-2 right-2 w-7 h-7 bg-danger/90 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Drag handle indicator */}
              <div className="absolute bottom-2 right-2 w-6 h-6 bg-midnight/70 text-cream/70 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                </svg>
              </div>
            </div>
          ))}

          {/* Upload slot in grid */}
          {canUploadMore && (
            <div
              {...getRootProps()}
              className={cn(
                'aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors',
                isDragActive
                  ? 'border-primary bg-primary/10'
                  : 'border-midnight-lighter hover:border-cream/30'
              )}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <svg className="w-6 h-6 text-cream/40 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="text-xs text-cream/40">Add</span>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty State / Upload Zone */}
      {photos.length === 0 && (
        <div
          {...getRootProps()}
          className={cn(
            'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
            isDragActive
              ? 'border-primary bg-primary/10'
              : error
              ? 'border-danger/50 bg-danger/5'
              : 'border-midnight-lighter hover:border-cream/30'
          )}
        >
          <input {...getInputProps()} />

          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Spinner size="lg" />
              <p className="text-cream/60">Uploading...</p>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 bg-primary/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-cream font-medium mb-1">
                {isDragActive ? 'Drop photos here' : 'Drag & drop photos'}
              </p>
              <p className="text-cream/50 text-sm mb-4">
                or click to browse
              </p>
              <p className="text-cream/40 text-xs">
                JPEG, PNG or WebP up to 10MB each
              </p>
            </>
          )}
        </div>
      )}

      {/* Photo count */}
      <div className="flex items-center justify-between text-sm">
        <span className={cn(
          photos.length === 0 ? 'text-cream/40' : 'text-cream/60'
        )}>
          {photos.length} of {maxPhotos} photos
        </span>
        {photos.length > 0 && (
          <span className="text-cream/40 text-xs">
            Drag to reorder • First photo is cover
          </span>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="text-danger text-sm">{error}</p>
      )}
    </div>
  );
}
